import { Cart, CartTotalResponse } from '@diamond/shared/types';
import { promoValidFromToFormat } from '@diamond/shared/utils';
import {
  AppliedVoucher as StoreAppliedVoucher,
  areCartProductsEqual,
  CART_TOTAL_QUERY_KEY,
  normalizeCartTotalPayload,
  selectCartProductsForTotals,
  useCartStore,
  useShipToStore,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import shallow from 'zustand/shallow';

import { formatDiscountDisplay } from '../banners/format-discount-display';

/**
 * Custom equality function for voucher codes
 * Prevents unnecessary re-renders when voucher array reference changes but content is same
 */
function areVoucherCodesEqual(
  vouchers1: StoreAppliedVoucher[],
  vouchers2: StoreAppliedVoucher[]
): boolean {
  if (vouchers1.length !== vouchers2.length) return false;
  return vouchers1.every((v, i) => v.code === vouchers2[i]?.code);
}

// Be resilient to optional server fields to avoid TS errors when shape narrows
type AppliedVoucher = NonNullable<CartTotalResponse['vouchers']> extends infer V
  ? V extends { applied_vouchers?: Array<unknown> }
    ? V['applied_vouchers'] extends Array<infer AV>
      ? AV
      : never
    : never
  : never;

type DiscountDisplayPromo = {
  type: 'discount';
  duration: string;
  text: string;
  promoId: string;
  discountDisplay?: string;
};

type DiscountPromoSource = {
  id: string;
  valid_from: number;
  valid_until: number;
  mechanism: string | null;
  is_applies_in_multiples?: boolean;
  deduction_amount?: number;
  is_discount?: boolean;
};

const buildDiscountDisplayPromo = <
  T extends DiscountPromoSource & Record<string, unknown>
>(
  promo: T
): DiscountDisplayPromo => {
  const appliesInMultiples =
    'is_applies_in_multiples' in promo &&
    Boolean(
      (promo as { is_applies_in_multiples?: boolean }).is_applies_in_multiples
    );

  const mechanismText = `${promo.mechanism ?? ''} ${
    appliesInMultiples ? '(Berlaku kelipatan)' : ''
  }`.trim();

  let discountDisplay: string | undefined;
  if (
    'deduction_amount' in promo &&
    typeof promo.deduction_amount === 'number' &&
    'is_discount' in promo &&
    typeof promo.is_discount === 'boolean'
  ) {
    discountDisplay = formatDiscountDisplay(
      promo.deduction_amount,
      promo.is_discount
    );
  }

  return {
    type: 'discount',
    duration: promoValidFromToFormat(promo.valid_from, promo.valid_until),
    text: mechanismText,
    promoId: promo.id,
    discountDisplay,
  };
};

export interface CartItemPromoData {
  dynamicPromos: Array<any>;
  displayPromos: Array<any>;
  hasPromos: boolean;
  isLoading: boolean;
  voucherInfo: AppliedVoucher | null;
}

export const useCartItemPromoDisplay = (
  item: Cart,
  isSelected: boolean
): CartItemPromoData => {
  const warehouse = useShipToStore((state) => state.selectedShipToId);
  const selectedProducts = useCartStore(
    selectCartProductsForTotals,
    areCartProductsEqual
  );
  // Use custom equality to prevent re-renders when voucher content hasn't changed
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    areVoucherCodesEqual
  );

  // Memoize voucher codes extraction to maintain stable reference
  const voucherCodes = useMemo(
    () => appliedVouchers.map((v) => v.code),
    [appliedVouchers]
  );

  const queryClient = useQueryClient();

  const payload = useMemo(
    () => ({
      warehouse_id: warehouse,
      is_return_promos: true,
      products: selectedProducts,
      voucher_codes: voucherCodes.length > 0 ? voucherCodes : undefined,
    }),
    [warehouse, selectedProducts, voucherCodes]
  );

  const debouncedPayload = useDebounce(payload, 500);
  const normalizedPayload = useMemo(
    () => normalizeCartTotalPayload(debouncedPayload),
    [debouncedPayload]
  );
  const queryKey = [CART_TOTAL_QUERY_KEY, normalizedPayload];
  const cartTotalData = queryClient.getQueryData<CartTotalResponse>(
    queryKey as unknown as [string, unknown]
  );

  const voucherInfo = useMemo(() => {
    if (!isSelected || !cartTotalData?.vouchers?.applied_vouchers) {
      return null;
    }

    const itemCode = item.product.item_code;
    const appliedVoucher = cartTotalData.vouchers.applied_vouchers.find(
      (voucher) =>
        voucher.product_discounts?.some(
          (discount) => discount.product_item_code === itemCode
        )
    );
    return appliedVoucher || null;
  }, [cartTotalData, item.product.item_code, isSelected]);

  const freeGoodsPromos = useCartStore(
    useCallback(
      (state) => state.freeGoodsPromoRecord[item.product.id] || [],
      [item.product.id]
    ),
    shallow
  );

  const discountPromos = useCartStore(
    useCallback(
      (state) => state.discountPromoRecord[item.product.id] || [],
      [item.product.id]
    ),
    shallow
  );

  const displayPromos = useMemo(() => {
    if (!isSelected) {
      return [];
    }

    const allPromos: Array<any> = [];

    if (voucherInfo) {
      const voucherDiscount = voucherInfo.product_discounts?.find(
        (discount) => discount.product_item_code === item.product.item_code
      );
      if (voucherDiscount) {
        allPromos.push({
          type: 'discount',
          duration: '',
          text: `Voucher ${voucherInfo.voucher_code}: Diskon ${voucherDiscount.discount_percent}%`,
          promoId: `voucher-${voucherInfo.voucher_id}`,
          isVoucher: true,
        });
      }

      const voucherFreeGood = voucherInfo.free_goods?.find(
        (fg) => fg.product_item_code === item.product.item_code
      );
      if (voucherFreeGood) {
        allPromos.push({
          type: 'free-item',
          duration: '',
          text: `Voucher ${voucherInfo.voucher_code}: Gratis ${
            voucherFreeGood.quantity / 1000
          }`,
          promoId: `voucher-freegoods-${voucherInfo.voucher_id}`,
          isVoucher: true,
        });
      }
    }

    if (!voucherInfo) {
      discountPromos.forEach((promo) => {
        allPromos.push(buildDiscountDisplayPromo(promo));
      });
    }

    if (
      !voucherInfo ||
      !voucherInfo.free_goods?.some(
        (fg) => fg.product_item_code === item.product.item_code
      )
    ) {
      freeGoodsPromos.forEach((promo) => {
        const images = promo.product_image_thumbnails || [];
        allPromos.push({
          type: 'free-item',
          duration: promoValidFromToFormat(promo.valid_from, promo.valid_until),
          text: `${promo.mechanism} ${
            'is_applies_in_multiples' in promo && promo.is_applies_in_multiples
              ? '(Berlaku kelipatan)'
              : ''
          }`.trim(),
          promoId: promo.id,
          item: {
            id: promo.product_id,
            price: promo.price,
            img: images[0],
            qty: promo.calculatedFreeQty / 1000,
            uom: promo.sales_unit_desc,
          },
          images: images.length > 0 ? images : undefined,
        });
      });
    }

    return allPromos;
  }, [
    isSelected,
    discountPromos,
    freeGoodsPromos,
    voucherInfo,
    item.product.item_code,
  ]);

  // For now, we'll disable the loading state to prevent infinite loading
  // The loading state should come from the parent component that uses useCartTotal
  const isLoading = false;

  return {
    dynamicPromos: [...discountPromos, ...freeGoodsPromos],
    displayPromos,
    hasPromos: displayPromos.length > 0,
    isLoading,
    voucherInfo,
  };
};
